import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { url } from 'src/environments/environment';
import { DashboardStore, LoginStore, SignupStore } from './store';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private currentUserSubject: BehaviorSubject<any>;
  public currentUser: Observable<any>;

  constructor(
    private http: HttpClient,
    private loginStore: LoginStore,
    private signupStore: SignupStore,
    private dashboardStore: DashboardStore
  ) {
    this.currentUserSubject = new BehaviorSubject<any>(
      this.dashboardStore.getCurrentUser()
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue() {
    return this.currentUserSubject.value;
  }

  private get accountId() {
    const accountId = this.dashboardStore.getAccountId();
    const currentUser = this.currentUserValue;
    if (currentUser.role === 'merchant') {
      if (
        currentUser.details.accounts.length === 0 &&
        this.dashboardStore.getIsNewAccountAdded() !== 'true'
      ) {
        return null;
      } else {
        if (accountId) {
          return JSON.parse(accountId);
        } else {
          this.dashboardStore.setAccountId(null);
          return JSON.parse(accountId);
        }
      }
    } else if (currentUser.role === 'cashier') {
      return currentUser.details.accountId;
    }
  }

  public get merchantId() {
    return this.currentUserValue.details.id;
  }

  login(email: string, password: string) {
    return this.http.post<any>(url.base + 'login', { email, password }).pipe(
      map((user) => {
        if (user && user.token) {
          // store user details and jwt token in local storage to keep user logged in between page refreshes
          this.dashboardStore.setCurrentUser(user);
          this.removeItems();
          this.currentUserSubject.next(user);
        }
        return user;
      })
    );
  }

  // Signup & Account
  createAccount(user: any) {
    if (this.signupStore.getIsAccountNew() !== 'true') {
      return this.http.post(url.base + 'merchant/register', user);
    } else {
      return this.http.post(url.base + 'accounts', user);
    }
  }

  getSummary(accountId: any) {
    return this.http.get(
      url.base + 'merchants/accounts/' + accountId + '/summary'
    );
  }

  getReferrals() {
    return this.http.get(url.base + 'merchants/referrals');
  }

  // Passwords and Verification
  forgotPassword(email: any) {
    return this.http.post(url.base + 'forgot-password', {
      email,
    });
  }

  changePassword(oldPassword: any, newPassword: any) {
    return this.http.patch(url.base + 'change-password', {
      oldPassword,
      newPassword,
    });
  }

  passwordReset(password: any, confirmPassword: any, token: any) {
    return this.http.patch(url.base + 'reset-password/' + token, {
      password,
      confirmPassword,
    });
  }

  verifyEmail(code: any) {
    return this.http.post(url.base + 'activation', {
      code,
    });
  }

  getNewCode(email: any) {
    return this.http.post(url.base + 'merchants/generate-code', {
      email,
    });
  }

  // Payments
  bankTransferPayment(amount: any, phone: any, description: any) {
    return this.http.post(url.base + 'transactions/bank-transfer', {
      amount,
      phone,
      description,
      accountId: this.accountId,
    });
  }

  ussdPayment(amount: any, bankCode: any) {
    return this.http.post(url.base + 'transactions/ussd-transfer', {
      amount,
      bankCode,
      accountId: this.accountId,
    });
  }

  confirmBankTransferPayment(virtualAccount: any) {
    return this.http.get(
      url.base + 'transactions/bank-transfer/confirm/' + virtualAccount
    );
  }

  confirmUssdPayment(reference: any) {
    return this.http.get(
      url.base + 'transactions/ussd-transfer/confirm/' + reference
    );
  }

  demoBankTransferPayment(
    amount: any,
    phone: any,
    description: any,
    accountName: any
  ) {
    return this.http.post(url.base + 'demo/transactions/bank-transfer', {
      amount,
      phone,
      description,
      accountName,
    });
  }

  demoUssdPayment(amount: any, bankCode: any) {
    return this.http.post(url.base + 'demo/transactions/ussd-transfer', {
      amount,
      bankCode,
    });
  }

  confirmDemoBankTransferPayment(virtualAccount: any) {
    return this.http.get(
      url.base + 'demo/transactions/bank-transfer/confirm/' + virtualAccount
    );
  }

  confirmDemoUssdPayment(reference: any) {
    return this.http.get(
      url.base + 'demo/transactions/ussd-transfer/confirm/' + reference
    );
  }

  // Withdrawals
  withdrawFunds(amount: any, password: any) {
    return this.http.post(
      url.base + 'merchants/accounts/' + this.accountId + '/transfer-money',
      {
        amount,
        password,
      }
    );
  }

  withdrawalHistory(date: any) {
    return this.http.get(
      url.base +
        'merchants/accounts/' +
        this.accountId +
        '/withdrawal-transactions/' +
        date
    );
  }

  // Cashiers and Dashboard
  getCashiers() {
    return this.http.get(url.base + 'cashiers/accounts/' + this.accountId);
  }

  addNewCashier(name: string, email: any, phone: number) {
    return this.http.post(url.base + 'cashiers', {
      name,
      email,
      phone,
      accountId: this.accountId,
    });
  }

  deleteCashier(id: number) {
    return this.http.patch(
      url.base + 'cashiers/' + id + '/accounts/' + this.accountId,
      {
        id,
      }
    );
  }

  getAmountReceived(pickerDate: any) {
    return this.http.get(
      url.base +
        'transactions/accounts/' +
        this.accountId +
        '/amount-received/?date=' +
        pickerDate
    );
  }

  getBalance() {
    return this.http.get(
      url.base + 'merchants/balance/accounts/' + this.accountId
    );
  }

  getTransactions(
    pickerDate: any,
    limit: number,
    offset: number,
    bankTransferCount: any,
    ussdCount: any,
    queryString: string
  ) {
    return this.http.get(
      url.base +
        'transactions/accounts/' +
        this.accountId +
        '/?date=' +
        pickerDate +
        '&limit=' +
        limit +
        '&offset=' +
        offset +
        '&bankCount=' +
        bankTransferCount +
        '&ussdCount=' +
        ussdCount +
        queryString
    );
  }

  // Reports
  getGeneralReports(pickerDate: any) {
    return this.http.get(
      url.base +
        'transactions/accounts/' +
        this.accountId +
        '/reports?date=' +
        pickerDate
    );
  }

  getCashiersReports(pickerDate: any) {
    return this.http.get(
      url.base +
        'transactions/accounts/' +
        this.accountId +
        '/reports?date=' +
        pickerDate +
        '&cashiers=all'
    );
  }

  // Incomplete Transactions
  createDispute(issue: any, other: any, reference: any) {
    return this.http.post(url.base + 'support/create-dispute', {
      issue,
      other,
      reference,
    });
  }

  resolve(reference: any) {
    return this.http.get(url.base + 'transactions/resolve/' + reference, {});
  }

  // Account Categorization (KYC)
  uploadDocument(DocumentType: string, FileName: string, Document: string, id: any) {
    return this.http.post(url.kyc + 'AccountDocument/Upload?accountUpgradeRequestId=' + id, {
      DocumentType,
      FileName,
      Document,
    });
  }

  upgradeGoldIndividual(goldIndividualForm: any) {
    return this.http.post(url.kyc + 'AccountUpgrade/Gold_Individual', {
      MerchantId: goldIndividualForm.MerchantId,
      FirstName: goldIndividualForm.FirstName,
      LastName: goldIndividualForm.LastName,
      Gender: goldIndividualForm.Gender,
      IdType: goldIndividualForm.IdType,
      Nin: goldIndividualForm.Nin,
      AccountIds: goldIndividualForm.AccountIds,
      AccountIdIsNull: goldIndividualForm.AccountIdIsNull,
      AccountUpgradeRequestId: goldIndividualForm.AccountUpgradeRequestId
    });
  }

  upgradeGoldBusiness(goldBusinessForm: any) {
    return this.http.post(url.kyc + 'AccountUpgrade/Gold_Business', {
      MerchantId: goldBusinessForm.MerchantId,
      IdType: goldBusinessForm.IdType,
      Nin: goldBusinessForm.Nin,
      Type: goldBusinessForm.Type,
      AccountIds: goldBusinessForm.AccountIds,
      AccountIdIsNull: goldBusinessForm.AccountIdIsNull,
      AccountUpgradeRequestId: goldBusinessForm.AccountUpgradeRequestId
    });
  }

  upgradePlatinum(platinumForm: any) {
    return this.http.post(url.kyc + 'AccountUpgrade/Platinium', {
      MerchantId: platinumForm.MerchantId,
      Director: {
        Bvn: platinumForm.Director.Bvn,
        Nin: platinumForm.Director.Nin,
      },
      PrincipalShareholder: {
        Bvn: platinumForm.PrincipalShareholder.Bvn,
        Nin: platinumForm.PrincipalShareholder.Nin,
      },
      Type: platinumForm.Type,
      AccountIds: platinumForm.AccountIds,
      AccountIdIsNull: platinumForm.AccountIdIsNull,
      AccountUpgradeRequestId: platinumForm.AccountUpgradeRequestId
    });
  }

  upgradeAccount(id: any) {
    return this.http.get(url.kyc + 'AccountUpgrade/Submit?merchantId=' + this.merchantId + '&accountUpgradeRequestId=' + id);
  }

  getUpgradeAccountIds() {
    return this.http.get(url.kyc + 'AccountUpgrade/AccountsWithUpgradeRequest?merchantId=' + this.merchantId);
  }

  getUpgradeStatus() {
    return this.http.get(url.kyc + 'AccountUpgrade/Status?merchantId=' + this.merchantId +
    (this.accountId !== null ? '&accountId=' + this.accountId : ''));
  }

  // Utility
  getBvn(bvn: any) {
    return this.http.get(url.base + 'utility/verify-bvn/' + bvn);
  }

  getBvn2(bvn: any) {
    return this.http.post(url.v2 + 'utility/verifyBvn', { bvn });
  }

  getBanks() {
    return this.http.get(url.base + 'utility/banks');
  }

  getUssdBanks() {
    return this.http.get(url.base + 'utility/banks?type=ussd');
  }

  getBankAccountName(accountNumber: any, bankCode: any) {
    return this.http.get(
      url.base +
        'utility/account-name-enquiry/' +
        accountNumber +
        '/bank/' +
        bankCode
    );
  }

  getStates() {
    return this.http.get(url.base + 'utility/states');
  }

  getAppVersion() {
    return this.http.get(
      'https://60fe99932574110017078690.mockapi.io/api/v1/version'
    );
  }

  registerDeviceId(registrationId: string) {
    return this.http.post(url.base + 'merchants/register-device', {
      registrationId,
    });
  }

  updateDeviceId(registrationId: string) {
    return this.http.patch(url.base + 'merchants/update-device-notification', {
      registrationId,
    });
  }

  removeItems() {
    this.signupStore.removeSignupItems();
  }

  logout() {
    // remove user from local storage and set current user to null
    this.currentUserValue.details.name = '';
    this.currentUserValue.role = '';
    this.currentUserSubject.next(null);
    this.removeItems();
    this.loginStore.removeItemsOnLogout();
    this.dashboardStore.setIsNewAccountAdded(false);
    this.dashboardStore.setPickerDate(this.dashboardStore.getPickerDate());
    this.loginStore.setPreviousEmail(this.loginStore.getLoginEmail());
  }
}
